import { useId, type ReactNode } from "react";
import {
  Controller,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues
} from "react-hook-form";

import { FieldShell } from "./FieldShell";
import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

type Props<T extends FieldValues, N extends FieldPath<T>> = Omit<
  BaseFieldProps<T>,
  "name"
> & {
  name: N;
  /** Renders the control; wire `field.value` / `field.onChange` / `field.ref`. */
  children: (
    field: ControllerRenderProps<T, N>,
    meta: { id: string; invalid: boolean; fieldState: ControllerFieldState }
  ) => ReactNode;
};

/** Label + error chrome around any custom control (pickers, toggles, swatches). */
export function ControlField<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  children
}: Props<T, N>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          id={id}
          label={label}
          required={required}
          description={description}
          error={fieldState.error}
          disabled={disabled}
          className={className}
        >
          {children(field, { id, invalid: fieldState.invalid, fieldState })}
        </FieldShell>
      )}
    />
  );
}
